/**
 * notificationsInbox.js — client-side writes to notifications/{uid}/items.
 *
 * This is the Phase B inbox surface — a Firestore-backed list of
 * recent activity the user can browse from /notifications. The
 * eventual server-side dispatcher (documented in
 * docs/runbooks/notification-dispatcher.md) will also write here,
 * but client-side writes are useful for the events we can detect
 * locally (e.g. a follow happened on this device → mirror an entry
 * for the followee).
 *
 * Schema:
 *   notifications/{uid}/items/{itemId} = {
 *     routeKey:   string,            // matches NOTIFICATION_ROUTES key
 *     title:      string,            // <70 chars
 *     body:       string,            // <240 chars
 *     icon:       string,            // route icon name (see notificationPrefs)
 *     data:       object,            // optional payload — e.g. { path: '/cellar/abc' }
 *     readAt:     timestamp | null,  // set when user opens the page
 *     createdAt:  timestamp,
 *     dedupKey:   string,            // OPTIONAL — for idempotency
 *   }
 *
 * Write paths in v1:
 *   • follow happens → writeFollowerNotification(followeeUid, followerUid, displayName)
 *
 * Future write paths (when dispatcher lands):
 *   • event cancellation / time change → eventChanges
 *   • friend rated wine ≥90 matching taste → friendHighRating
 *   • household added matching wine → householdNewMatch
 *   • WOTD picked → wotd
 *   • etc.
 *
 * Why client-side for now:
 *   We don't have Firestore triggers wired (no Cloud Functions yet).
 *   The dispatcher will eventually own this, but doing the simple
 *   case (follower notification on the device that initiated the
 *   follow) lets us prove the inbox UX end-to-end without
 *   infrastructure work.
 */

import {
  addDoc, collection, deleteDoc, doc, getDocs, limit, onSnapshot,
  orderBy, query, serverTimestamp, updateDoc, where, writeBatch,
} from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { db } from '../firebase.js'

// ── Pref gate ────────────────────────────────────────────────────────
// Server-side dispatcher MUST check notificationPrefs.{route}.inApp
// before writing here. Client-side callers should also check, but the
// follow code below skips the check — a follow is mutual social
// activity and the followee should know regardless of their default
// pref. (User can still clear it from the inbox.)
//
// All write helpers return the resulting docId or null on failure.

/**
 * Mirror a notification to the recipient's inbox. Generic helper used
 * by the route-specific helpers below + future server-side dispatcher
 * (when callable via Admin SDK).
 *
 * `dedupKey` is optional but recommended for events that could fire
 * twice (e.g. unfollow + re-follow within seconds shouldn't double-
 * up). When provided, we look for an existing item with the same
 * dedupKey in the last 24h before writing.
 */
export async function writeInboxItem(recipientUid, payload) {
  if (!recipientUid || !payload?.routeKey) return null
  try {
    const ref = collection(db, 'notifications', recipientUid, 'items')

    // Optional dedup window — skip the write if we already mirrored
    // this exact event recently.
    if (payload.dedupKey) {
      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
      const dupCheck = await getDocs(query(
        ref,
        where('dedupKey', '==', payload.dedupKey),
        where('createdAt', '>=', dayAgo),
        limit(1),
      ))
      if (!dupCheck.empty) return null
    }

    const docRef = await addDoc(ref, {
      routeKey:  payload.routeKey,
      title:     String(payload.title || '').slice(0, 80),
      body:      String(payload.body  || '').slice(0, 280),
      icon:      payload.icon || null,
      data:      payload.data || {},
      readAt:    null,
      createdAt: serverTimestamp(),
      // VIN-2026-01 (2026-05-26): stamp fromUserId when caller
      // supplies it so the tightened firestore rule can attribute +
      // gate cross-user inbox writes. Server-side (Admin SDK) writes
      // bypass rules; this is for client-initiated writes only.
      ...(payload.fromUserId ? { fromUserId: payload.fromUserId } : {}),
      ...(payload.dedupKey ? { dedupKey: payload.dedupKey } : {}),
    })
    return docRef.id
  } catch (err) {
    console.warn('[notificationsInbox] writeInboxItem failed:', err?.code || err?.message)
    return null
  }
}

// ── Route-specific helpers ───────────────────────────────────────────

/**
 * "{Display Name} followed your cellar" inbox entry. Called from the
 * client that just executed the follow — see follows.js follow().
 *
 * VIN-2026-01 (2026-05-26): now stamps fromUserId = followerUid so
 * the tightened inbox rule can verify the writer is the follower
 * (the firestore rule cross-user branch requires
 * request.resource.data.fromUserId == request.auth.uid).
 */
export async function writeFollowerNotification(followeeUid, followerUid, followerName) {
  return writeInboxItem(followeeUid, {
    routeKey:   'friendInvites', // closest existing route — covers any new social connection
    fromUserId: followerUid,     // required by VIN-2026-01 rule
    title:      `${followerName || 'A new cellar'} is now following you`,
    body:       'Tap to see their cellar.',
    icon:       'people',
    data:       { path: `/cellar/${followerUid}` },
    dedupKey:   `follow:${followerUid}:${followeeUid}`,
  })
}

// ── Reads ────────────────────────────────────────────────────────────

/**
 * Live subscription to the user's recent inbox items. Returns the
 * newest 50 by default.
 *
 * Each item: { id, routeKey, title, body, icon, data, readAt, createdAt }
 */
export function useInbox(uid, max = 50) {
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!uid) { setItems([]); setLoading(false); return }
    const q = query(
      collection(db, 'notifications', uid, 'items'),
      orderBy('createdAt', 'desc'),
      limit(max),
    )
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })))
      setLoading(false)
    }, (err) => {
      console.warn('[useInbox] subscribe failed:', err?.code || err?.message)
      setLoading(false)
    })
    return () => unsub()
  }, [uid, max])

  return { items, loading, unreadCount: items.filter(i => !i.readAt).length }
}

/**
 * Mark a single item read.
 */
export async function markItemRead(uid, itemId) {
  if (!uid || !itemId) return
  try {
    await updateDoc(doc(db, 'notifications', uid, 'items', itemId), {
      readAt: serverTimestamp(),
    })
  } catch (err) {
    console.warn('[notificationsInbox] markItemRead failed:', err?.code || err?.message)
  }
}

/**
 * Mark all unread items read in one batch.
 */
export async function markAllRead(uid) {
  if (!uid) return
  try {
    const snap = await getDocs(query(
      collection(db, 'notifications', uid, 'items'),
      where('readAt', '==', null),
    ))
    if (snap.empty) return
    const batch = writeBatch(db)
    snap.docs.forEach(d => batch.update(d.ref, { readAt: serverTimestamp() }))
    await batch.commit()
  } catch (err) {
    console.warn('[notificationsInbox] markAllRead failed:', err?.code || err?.message)
  }
}

/**
 * Delete a single item. The user might unfollow + delete the matching
 * inbox item, or just clear stale items.
 */
export async function deleteItem(uid, itemId) {
  if (!uid || !itemId) return
  try {
    await deleteDoc(doc(db, 'notifications', uid, 'items', itemId))
  } catch (err) {
    console.warn('[notificationsInbox] deleteItem failed:', err?.code || err?.message)
  }
}
